import React, { useState, useEffect } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

interface EmailConfig {
  enabled: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
  smtpPassword: string;
  fromName: string;
  fromEmail: string;
  replyTo: string;
  signature: string;
}

const EmailSettings: React.FC = () => {
  const [config, setConfig] = useState<EmailConfig>({
    enabled: false,
    smtpHost: '',
    smtpPort: 587,
    smtpSecure: true,
    smtpUser: '',
    smtpPassword: '',
    fromName: '',
    fromEmail: '',
    replyTo: '',
    signature: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Load existing config
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const password = localStorage.getItem('admin-password') || '';
        const res = await fetch('/api/admin/email-config', {
          headers: { 'x-admin-password': password }
        });
        
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.config) {
            setConfig(data.config);
          }
        }
      } catch (error) {
        console.error('Failed to load email config:', error);
      }
    };

    loadConfig();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const password = localStorage.getItem('admin-password') || '';
      const res = await fetch('/api/admin/email-config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-password': password
        },
        body: JSON.stringify(config)
      });

      const result = await res.json();
      
      if (result.success) {
        setMessage({ type: 'success', text: 'Email settings saved successfully!' });
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to save settings' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save email settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setMessage(null);

    try {
      const password = localStorage.getItem('admin-password') || '';
      const res = await fetch('/api/admin/email-test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-password': password
        },
        body: JSON.stringify({
          to: config.fromEmail || config.replyTo,
          subject: 'Email Configuration Test',
          text: 'This is a test email to verify your email configuration is working correctly.'
        })
      });

      const result = await res.json();
      
      if (result.success) {
        setMessage({ type: 'success', text: 'Test email sent successfully!' });
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to send test email' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to send test email' });
    } finally {
      setTesting(false);
    }
  };

  const commonProviders = [
    { name: 'Gmail', host: 'smtp.gmail.com', port: 587, secure: true },
    { name: 'Outlook/Hotmail', host: 'smtp-mail.outlook.com', port: 587, secure: false },
    { name: 'Yahoo', host: 'smtp.mail.yahoo.com', port: 587, secure: false },
    { name: 'iCloud', host: 'smtp.mail.me.com', port: 587, secure: false },
  ];

  const setProvider = (provider: typeof commonProviders[0]) => {
    setConfig(prev => ({
      ...prev,
      smtpHost: provider.host,
      smtpPort: provider.port,
      smtpSecure: provider.secure
    }));
  };

  return (
    <div className="space-y-6">
      {message && (
        <div className={`p-3 rounded-lg text-sm ${
          message.type === 'success' 
            ? 'bg-green-500/20 border border-green-500/40 text-green-300'
            : 'bg-red-500/20 border border-red-500/40 text-red-300'
        }`}>
          {message.text}
        </div>
      )}

      {/* Enable/Disable Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h5 className="text-white font-medium">Enable Email System</h5>
          <p className="text-gray-400 text-sm">Send emails directly from the admin panel</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => setConfig(prev => ({ ...prev, enabled: e.target.checked }))}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
        </label>
      </div>

      {config.enabled && (
        <>
          {/* Quick Setup */}
          <div>
            <h5 className="text-white font-medium mb-3">Quick Setup</h5>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {commonProviders.map(provider => (
                <button
                  key={provider.name}
                  onClick={() => setProvider(provider)}
                  className="px-3 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg text-white text-sm transition-colors"
                >
                  {provider.name}
                </button>
              ))}
            </div>
          </div>

          {/* SMTP Configuration */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 text-sm mb-2">SMTP Host</label>
              <input
                type="text"
                value={config.smtpHost}
                onChange={(e) => setConfig(prev => ({ ...prev, smtpHost: e.target.value }))}
                placeholder="smtp.gmail.com"
                className="w-full p-3 bg-gray-600 rounded-lg text-white placeholder-gray-400"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm mb-2">SMTP Port</label>
              <input
                type="number"
                value={config.smtpPort}
                onChange={(e) => setConfig(prev => ({ ...prev, smtpPort: Number(e.target.value) }))}
                placeholder="587"
                className="w-full p-3 bg-gray-600 rounded-lg text-white placeholder-gray-400"
              />
            </div>
          </div>

          {/* Security */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="smtpSecure"
              checked={config.smtpSecure}
              onChange={(e) => setConfig(prev => ({ ...prev, smtpSecure: e.target.checked }))}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="smtpSecure" className="ml-2 text-gray-300 text-sm">
              Use TLS/SSL encryption
            </label>
          </div>

          {/* Authentication */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 text-sm mb-2">Username/Email</label>
              <input
                type="email"
                value={config.smtpUser}
                onChange={(e) => setConfig(prev => ({ ...prev, smtpUser: e.target.value }))}
                placeholder="your-email@gmail.com"
                className="w-full p-3 bg-gray-600 rounded-lg text-white placeholder-gray-400"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm mb-2">Password/App Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={config.smtpPassword}
                  onChange={(e) => setConfig(prev => ({ ...prev, smtpPassword: e.target.value }))}
                  placeholder="Your app password"
                  className="w-full p-3 bg-gray-600 rounded-lg text-white placeholder-gray-400 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                For Gmail, use an App Password instead of your regular password
              </p>
            </div>
          </div>

          {/* Sender Information */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 text-sm mb-2">From Name</label>
              <input
                type="text"
                value={config.fromName}
                onChange={(e) => setConfig(prev => ({ ...prev, fromName: e.target.value }))}
                placeholder="Jean Bilbao"
                className="w-full p-3 bg-gray-600 rounded-lg text-white placeholder-gray-400"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm mb-2">From Email</label>
              <input
                type="email"
                value={config.fromEmail}
                onChange={(e) => setConfig(prev => ({ ...prev, fromEmail: e.target.value }))}
                placeholder="jean@example.com"
                className="w-full p-3 bg-gray-600 rounded-lg text-white placeholder-gray-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-300 text-sm mb-2">Reply-To Email (optional)</label>
            <input
              type="email"
              value={config.replyTo}
              onChange={(e) => setConfig(prev => ({ ...prev, replyTo: e.target.value }))}
              placeholder="replies@example.com"
              className="w-full p-3 bg-gray-600 rounded-lg text-white placeholder-gray-400"
            />
          </div>

          {/* Email Signature */}
          <div>
            <label className="block text-gray-300 text-sm mb-2">Email Signature</label>
            <textarea
              value={config.signature}
              onChange={(e) => setConfig(prev => ({ ...prev, signature: e.target.value }))}
              placeholder="Best regards,&#10;Jean Bilbao&#10;AI & Automation Consultant"
              rows={4}
              className="w-full p-3 bg-gray-600 rounded-lg text-white placeholder-gray-400"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>

            <button
              onClick={handleTest}
              disabled={testing || !config.smtpHost || !config.smtpUser}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50 transition-colors"
            >
              {testing ? 'Testing...' : 'Send Test Email'}
            </button>
          </div>

          {/* Help Text */}
          <div className="bg-gray-600/50 rounded-lg p-4">
            <h6 className="text-white font-medium mb-2">Setup Tips:</h6>
            <ul className="text-gray-300 text-sm space-y-1">
              <li>• For Gmail: Enable 2FA and create an App Password</li>
              <li>• For Outlook: Use your regular password unless 2FA is enabled</li>
              <li>• Test the configuration before relying on it</li>
              <li>• All settings are encrypted and stored securely</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default EmailSettings;
